import React, { useState, useEffect, useRef } from 'react';
import { ANALYSE_TIPPS } from '../data/analyseTipps';
import { generateAndDownloadPDF } from '../utils/pdfGenerate';
import MobileCVVorschau from './MobileCVVorschau';
import MobileAnschreibenVorschau from './MobileAnschreibenVorschau';
import {
  getProfile, getCVVorlagen, getAnschreibenVorlagen,
  starteAnalyse, starteGenerierung, retryBewerbung, getBewerbungStatus, getStellen, updateStelle,
  getProfil,
} from '../api';
import { capture } from '../tracking/posthog';
import './MobileStelleFlow.css';

// ─── Module Imports ────────────────────────────────────────────────────────
import { D } from '../mobile_stelle_flow/FlowTokens';
import { STATUS_OPTIONEN } from '../mobile_stelle_flow/FlowConstants';
import { 
  IconChevronLeft, 
  IconChevronDown, 
  StatusSendeIcon 
} from '../mobile_stelle_flow/FlowIcons';
import { 
  formatDateiname, 
  parseAnalyseText 
} from '../mobile_stelle_flow/FlowUtils';
import { SelectRow } from '../mobile_stelle_flow/FlowComponents';
import { buildFitVerdict, fitScoreLabel } from '../utils/fitScores';

function MobileStelleFlow({ stelle, tonlage, onZurueck, onGeaendert, nutzer, autoStart = false, onSektionBearbeiten, aktivesProfile, onKeinKontingent, sheetIsOpen = false }) {
  const [ansicht, setAnsicht] = useState(
    (stelle?.cv_data || stelle?.cv_rohtext || stelle?.generiert_am) ? 'ergebnis'
    : (stelle?.analyse || autoStart) ? 'analyse'
    : 'eingabe'
  );
  // Eingabe state
  const [modus, setModus] = useState(stelle?.url ? 'url' : 'text');
  const [text, setText] = useState(stelle?.text || '');
  const [url, setUrl] = useState(stelle?.url || '');

  // API data
  const [profile, setProfile] = useState([]);
  const [profilName, setProfilName] = useState('');
  const [cvVorlagen, setCvVorlagen] = useState([]);
  const [anVorlagen, setAnVorlagen] = useState([]);
  const [cvVorlage, setCvVorlage] = useState('');
  const [anVorlage, setAnVorlage] = useState('');

  // Job status
  const hasDocuments = stelle?.cv_data || stelle?.cv_rohtext || stelle?.generiert_am;
  const [status, setStatus] = useState(
    autoStart ? 'analyse'
    : (!hasDocuments && stelle?.analyse) ? 'warten'
    : null
  );
  const [schritt, setSchritt] = useState('');
  const [schrittIndex, setSchrittIndex] = useState(null);
  const [schrittTotal, setSchrittTotal] = useState(null);
  const [tipIndex, setTipIndex] = useState(0);
  const [analyse, setAnalyse] = useState((!hasDocuments && stelle?.analyse) ? stelle.analyse : null);
  const [analyseStruktur, setAnalyseStruktur] = useState((!hasDocuments && stelle?.analyse_struktur) ? stelle.analyse_struktur : null);
  const [jobId, setJobId] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [retryMoeglich, setRetryMoeglich] = useState(false);
  const [, setFortschritt] = useState(0);

  // Ergebnis screen UI state
  const [fitOffen, setFitOffen] = useState(false);
  const [scoreSubOffen, setScoreSubOffen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [dokSlide, setDokSlide] = useState(0); // 0 = CV, 1 = Anschreiben
  const [statusDropdownOffen, setStatusDropdownOffen] = useState(false);
  const statusBtnRef = useRef(null);
  const swipeRef = useRef(null);
  const dokSwipeRef = useRef(null);
  const dokTouchStartX = useRef(null);
  const dokTouchStartY = useRef(null);
  const dokTouchLastX = useRef(null);
  const dokDirLock = useRef(null); // 'h' | 'v' | null
  const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0 });

  // Simulated loading progress for the detail view
  const [simProgress, setSimProgress] = useState(12);
  const simRef = useRef(null);

  const [aktuellesProfil, setAktuellesProfil] = useState(null);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const firmenNameRef = useRef(null);
  const stelleRef = useRef(stelle);
  const cvRef = useRef(null);
  const anRef = useRef(null);
  const autoStartedRef = useRef(false);

  useEffect(() => { stelleRef.current = stelle; }, [stelle]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const name = stelle?.profil_name || profilName;
    if (!name) { setAktuellesProfil(null); return; }
    getProfil(name)
      .then((r) => setAktuellesProfil(r.data))
      .catch(() => setAktuellesProfil(null));
  }, [stelle?.profil_name, profilName]);

  // Die automatische Kürzung ist am 2026-08-09 auf Wunsch des Users entfernt worden —
  // hier genauso wie im Editor. Sie ersetzte den ganzen Brieftext ungefragt durch eine
  // KI-Antwort, sobald eine zweite Seite entstand. Nicht wieder einbauen.

  useEffect(() => {
    getProfile().then((r) => {
      setProfile(r.data);
      if (r.data.length > 0) setProfilName((r.data.find(p => p.aktiv) ?? r.data[0]).dateiname);
    });
    getCVVorlagen().then((r) => {
      setCvVorlagen(r.data);
      const def = r.data.find(v => v.dateiname === 'cv_standard') || r.data[0];
      if (def) setCvVorlage(def.dateiname);
    });
    getAnschreibenVorlagen().then((r) => {
      setAnVorlagen(r.data);
      if (r.data.length > 0) setAnVorlage(r.data[0].dateiname);
    });
  }, []);

  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (profilName && cvVorlage && anVorlage && (stelle?.url || stelle?.text)) {
      autoStartedRef.current = true;
      onAnalyseStartenMitDaten(profilName, cvVorlage, anVorlage);
    }
  }, [profilName, cvVorlage, anVorlage]);

  useEffect(() => {
    if (status === 'analyse' || status === 'generiert') {
      setSimProgress(12);
      simRef.current = setInterval(() => {
        setSimProgress(p => {
          // Echt-Phasen-Decke: bis zu 95 % proportional zum aktuellen Schritt
          const target = schrittIndex != null && schrittTotal
            ? Math.round((schrittIndex / schrittTotal) * 95)
            : 88;
          const next = p + (p < 40 ? 3 : p < 70 ? 1.5 : 0.4);
          return Math.min(next, target);
        });
      }, 180);
    } else if (status === null || status === 'warten' || status === 'fehler') {
      clearInterval(simRef.current);
    }
    return () => clearInterval(simRef.current);
  }, [status, schrittIndex, schrittTotal]);

  // Tipp-Rotation während Analyse/Generierung läuft
  const tipRef = useRef(null);
  useEffect(() => {
    if (status === 'analyse' || status === 'generiert') {
      setTipIndex(0);
      tipRef.current = setInterval(() => {
        setTipIndex(i => (i + 1) % ANALYSE_TIPPS.length);
      }, 4500);
    } else {
      clearInterval(tipRef.current);
    }
    return () => clearInterval(tipRef.current);
  }, [status]);

  useEffect(() => {
    if (!stelle) return;
    setText(stelle.text || '');
    setUrl(stelle.url || '');
    setModus(stelle.url ? 'url' : 'text');
    if (!stelle.cv_data && !stelle.cv_rohtext && !stelle.generiert_am && !autoStart) {
      if (stelle.analyse) {
        setAnsicht('analyse');
        setAnalyse(stelle.analyse);
        setAnalyseStruktur(stelle.analyse_struktur || null);
        setStatus('warten');
      } else {
        setAnsicht('eingabe');
        setStatus(null);
        setJobId(null);
        setFehler(null);
        setAnalyse(null);
        setAnalyseStruktur(null);
      }
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, [stelle?.id]);

  useEffect(() => {
    if (!fitOffen) return;
    const el = swipeRef.current;
    if (!el) return;
    let startX = 0;
    const onStart = (e) => { startX = e.touches[0]?.clientX ?? 0; };
    const onMove = (e) => {
      if (Math.abs((e.touches[0]?.clientX ?? startX) - startX) > 5) e.preventDefault();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  }, [fitOffen]);

  useEffect(() => {
    if (!statusDropdownOffen) return;
    const handler = (e) => {
      if (statusBtnRef.current && !statusBtnRef.current.contains(e.target)) {
        setStatusDropdownOffen(false);
      }
    };
    document.addEventListener('touchstart', handler);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [statusDropdownOffen]);

  const profil = formatDateiname(stelle?.profil_name || '');
  const unternehmen = formatDateiname(stelle?.name || '');
  const cvName = profil && unternehmen ? `CV_${profil}_${unternehmen}.pdf` : 'lebenslauf.pdf';
  const anName = profil && unternehmen ? `AN_${profil}_${unternehmen}.pdf` : 'anschreiben.pdf';
  const [pdfLaedt, setPdfLaedt] = useState({ cv: false, an: false });

  const druckCV = async () => {
    if (!cvRef.current) return;
    setPdfLaedt((s) => ({ ...s, cv: true }));
    try { await generateAndDownloadPDF(cvRef.current, cvName); }
    finally { setPdfLaedt((s) => ({ ...s, cv: false })); }
  };

  const druckAN = async () => {
    if (!anRef.current) return;
    setPdfLaedt((s) => ({ ...s, an: true }));
    try { await generateAndDownloadPDF(anRef.current, anName); }
    finally { setPdfLaedt((s) => ({ ...s, an: false })); }
  };

  const startePolling = (id) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const intervalId = setInterval(async () => {
      try {
        const s = await getBewerbungStatus(id);
        setSchritt(s.data.schritt || '');
        if (s.data.schritt_index != null) setSchrittIndex(s.data.schritt_index);
        if (s.data.schritt_total != null) setSchrittTotal(s.data.schritt_total);
        setRetryMoeglich(Boolean(s.data.retry_moeglich));
        if (s.data.analyse) {
          setAnalyse(s.data.analyse);
          setAnalyseStruktur(s.data.analyse_struktur || null);
        }
        if (s.data.status === 'fertig') {
          clearInterval(intervalId);
          if (intervalRef.current === intervalId) intervalRef.current = null;
          capture('job_generation_completed', { source: modus === 'url' ? 'job_url' : 'manual_input', success: true });
          setStatus('fertig');
          setAnsicht('ergebnis');
          try {
            const alleStellen = await getStellen();
            const frisch = alleStellen.data.find((x) => x.id === stelleRef.current.id);
            if (frisch) onGeaendert(frisch, 'update');
          } catch {
            if (s.data.firmen_name) onGeaendert({ ...stelleRef.current, name: s.data.firmen_name }, 'update');
          }
        } else if (s.data.status === 'fehler') {
          clearInterval(intervalId);
          if (intervalRef.current === intervalId) intervalRef.current = null;
          setStatus('fehler');
          setFehler(s.data.fehler || 'Generierung fehlgeschlagen.');
        }
      } catch {
        clearInterval(intervalId);
        if (intervalRef.current === intervalId) intervalRef.current = null;
        setStatus('fehler');
        setFehler('Verbindung zum Server unterbrochen.');
      }
    }, 3000);
    intervalRef.current = intervalId;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalId);
      if (intervalRef.current === intervalId) intervalRef.current = null;
      setStatus(st => st === 'generiert' ? 'fehler' : st);
      setFehler(f => f || 'Zeitüberschreitung — bitte erneut versuchen.');
    }, 5 * 60 * 1000);
  };

  // Einheitlicher Poll für One-Click-Flow: Analyse + Generierung bis fertig
  const _starteKomplettPolling = (id) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const intervalId = setInterval(async () => {
      try {
        const s = await getBewerbungStatus(id);
        setSchritt(s.data.schritt || '');
        if (s.data.schritt_index != null) setSchrittIndex(s.data.schritt_index);
        if (s.data.schritt_total != null) setSchrittTotal(s.data.schritt_total);
        setRetryMoeglich(Boolean(s.data.retry_moeglich));
        if (s.data.firmen_name && stelleRef.current?.name !== s.data.firmen_name) {
          onGeaendert({ ...stelleRef.current, name: s.data.firmen_name }, 'update');
        }
        if (s.data.analyse) {
          setAnalyse(s.data.analyse);
          setAnalyseStruktur(s.data.analyse_struktur || null);
          setStatus(st => st === 'analyse' ? 'generiert' : st);
        }
        if (s.data.kein_kontingent) {
          clearInterval(intervalId);
          if (intervalRef.current === intervalId) intervalRef.current = null;
          setStatus('warten');
          setFortschritt(1);
          if (onKeinKontingent) onKeinKontingent();
          return;
        }
        if (s.data.status === 'fertig') {
          clearInterval(intervalId);
          if (intervalRef.current === intervalId) intervalRef.current = null;
          setStatus('fertig');
          setAnsicht('ergebnis');
          try {
            const alleStellen = await getStellen();
            const frisch = alleStellen.data.find((x) => x.id === stelleRef.current.id);
            if (frisch) onGeaendert(frisch, 'update');
          } catch {
            if (s.data.firmen_name) onGeaendert({ ...stelleRef.current, name: s.data.firmen_name }, 'update');
          }
        } else if (s.data.status === 'fehler') {
          clearInterval(intervalId);
          if (intervalRef.current === intervalId) intervalRef.current = null;
          setStatus('fehler');
          setFehler(s.data.fehler || 'Generierung fehlgeschlagen.');
        }
      } catch {
        clearInterval(intervalId);
        if (intervalRef.current === intervalId) intervalRef.current = null;
        setStatus('fehler');
        setFehler('Verbindung zum Server unterbrochen.');
      }
    }, 3000);
    intervalRef.current = intervalId;
    // 8 Minuten Timeout (5 Analyse + 3 Generierung)
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalId);
      if (intervalRef.current === intervalId) intervalRef.current = null;
      setStatus(st => (st === 'analyse' || st === 'generiert') ? 'fehler' : st);
      setFehler(f => f || 'Zeitüberschreitung — bitte erneut versuchen.');
    }, 8 * 60 * 1000);
  };

  const onAnalyseStartenMitDaten = async (pName, cvV, anV) => {
    if (!stelle) return;
    setFehler(null);
    setRetryMoeglich(false);
    setStatus('analyse');
    setAnalyse(null);
    setAnalyseStruktur(null);
    setFortschritt(1);
    setAnsicht('analyse');

    const currentStelle = stelleRef.current;
    try {
      await updateStelle(currentStelle.id, { text: currentStelle.text || '', url: currentStelle.url || '' });
      const res = await starteAnalyse({
        profil_name: pName,
        stellen_text: currentStelle.url ? undefined : (currentStelle.text || ''),
        stellen_url: currentStelle.url || undefined,
        stelle_id: currentStelle.id,
        cv_vorlage: cvV,
        anschreiben_vorlage: anV,
        tonlage: tonlage || 'klassisch',
      });
      const id = res.data.job_id;
      setJobId(id);
      _starteKomplettPolling(id);
    } catch (err) {
      console.error('[CVFlow] onAnalyseStartenMitDaten Fehler:', err?.response?.status, err?.response?.data, err?.message);
      setStatus('fehler');
      setFehler(err?.response?.data?.detail || err?.message || 'Analyse fehlgeschlagen.');
    } finally {
      onGeaendert({ ...currentStelle }, 'update');
    }
  };

  const onAnalyseStarten = async () => {
    if (!stelle) return;
    setFehler(null);
    setRetryMoeglich(false);
    setStatus('analyse');
    setAnalyse(null);
    setAnalyseStruktur(null);
    setFortschritt(1);
    setAnsicht('analyse');

    try {
      await updateStelle(stelle.id, { text, url });
      const res = await starteAnalyse({
        profil_name: profilName,
        stellen_text: modus === 'text' ? text : undefined,
        stellen_url: modus === 'url' ? url : undefined,
        stelle_id: stelle.id,
        cv_vorlage: cvVorlage,
        anschreiben_vorlage: anVorlage,
        tonlage: tonlage || 'klassisch',
      });
      const id = res.data.job_id;
      capture('job_generation_started', { source: modus === 'url' ? 'job_url' : 'manual_input' });
      setJobId(id);
      _starteKomplettPolling(id);
    } catch (err) {
      console.error('[CVFlow] onAnalyseStarten Fehler:', err?.response?.status, err?.response?.data, err?.message);
      setStatus('fehler');
      setFehler(err?.response?.data?.detail || err?.message || 'Analyse fehlgeschlagen.');
    } finally {
      onGeaendert({ ...stelle, text, url }, 'update');
    }
  };

  const onWeiterGenerieren = async () => {
    if (!jobId) {
      onAnalyseStarten();
      return;
    }
    setStatus('generiert');
    setFortschritt(2);
    setRetryMoeglich(false);
    try {
      await starteGenerierung({
        job_id: jobId,
        stelle_id: stelleRef.current?.id,
        profil_name: profilName,
        cv_vorlage: cvVorlage,
        anschreiben_vorlage: anVorlage,
        tonlage: tonlage || 'klassisch',
      });
      startePolling(jobId);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 402) {
        if (onKeinKontingent) onKeinKontingent();
        setStatus('warten');
      } else {
        setStatus('fehler');
        setFehler(detail || 'Generierung fehlgeschlagen.');
      }
    }
  };

  const onRetryGenerieren = async () => {
    if (!jobId) return;
    setStatus('generiert');
    setFehler(null);
    setRetryMoeglich(false);
    setFortschritt(2);
    try {
      await retryBewerbung(jobId);
      startePolling(jobId);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      setStatus('fehler');
      setFehler(detail || 'Erneuter Versuch fehlgeschlagen.');
    }
  };

  const onAbbrechen = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (firmenNameRef.current) clearInterval(firmenNameRef.current);
    setStatus(null);
    setJobId(null);
    setAnalyse(null);
    setAnalyseStruktur(null);
    setFehler(null);
    setRetryMoeglich(false);
    setAnsicht('eingabe');
  };

  const initialen = nutzer?.displayName ? nutzer.displayName.charAt(0).toUpperCase() : '?';
  const kannStarten = profilName && cvVorlage && anVorlage && (text.trim() || url.trim());

  const scoreFill = (row) => {
    if (row.gradient) return row.gradient;
    if (row.className === 'machbarkeit') return 'linear-gradient(90deg,#00A078,#004D40)';
    if (row.className === 'profilnaehe') return 'linear-gradient(90deg,#00B5FC,#006064)';
    if (row.className === 'strategie') return 'linear-gradient(90deg,#FFBC00,#E6A800)';
    if (row.tone === 'weak') return 'linear-gradient(90deg,#FCA5A5,#DC2626)';
    if (row.tone === 'mixed') return 'linear-gradient(90deg,#E8B84B,#E85A4B)';
    return 'linear-gradient(90deg,#FFBC00,#FF6600)';
  };

  const renderScoreBars = (rows, compact = false) => (
    <div style={{ display: 'grid', gap: compact ? 8 : 10 }}>
      {rows.map(row => (
        <div
          key={row.key}
          title={row.info}
          aria-label={`${row.label}: ${row.display}. ${row.info}`}
          style={{
            padding: compact ? '8px 9px' : '10px 11px',
            borderRadius: 14,
            background: 'linear-gradient(135deg, rgba(255,252,246,0.96), rgba(245,242,233,0.72))',
            border: `1px solid ${D.border}`,
            boxShadow: '0 8px 18px rgba(42,34,20,0.06), inset 0 1px 0 rgba(255,255,255,0.72)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: D.text }}>{row.label}</span>
            <span style={{ width: 15, height: 15, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,77,64,0.08)', color: D.textMuted, fontSize: 9, fontWeight: 900 }}>?</span>
            <span style={{ justifySelf: 'end', fontFamily: D.mono, fontSize: 12, fontWeight: 800, color: row.color || D.text }}>{row.display}</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, overflow: 'hidden', background: 'rgba(0,77,64,0.08)', border: '1px solid rgba(0,77,64,0.08)' }}>
            <div style={{ width: `${row.percent}%`, height: '100%', borderRadius: 999, background: scoreFill(row), transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderCollapsibleScoreBars = (rows, compact = false) => {
    if (!rows || rows.length <= 1) return renderScoreBars(rows, compact);
    const main = rows[0];
    const subs = rows.slice(1);
    const pad = compact ? '8px 9px' : '10px 11px';
    return (
      <div>
        <div
          onClick={() => setScoreSubOffen(o => !o)}
          title={main.info}
          aria-label={`${main.label}: ${main.display}. ${main.info}`}
          style={{
            padding: pad, borderRadius: 14, userSelect: 'none',
            background: 'linear-gradient(135deg, rgba(255,252,246,0.96), rgba(245,242,233,0.72))',
            border: `1px solid ${D.border}`,
            boxShadow: '0 8px 18px rgba(42,34,20,0.06), inset 0 1px 0 rgba(255,255,255,0.72)',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: D.text }}>{main.label}</span>
            <span style={{ width: 15, height: 15, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,77,64,0.08)', color: D.textMuted, fontSize: 9, fontWeight: 900 }}>?</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: D.mono, fontSize: 12, fontWeight: 800, color: main.color || D.text, marginRight: 4 }}>{main.display}</span>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              style={{ flexShrink: 0, transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)', transform: scoreSubOffen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <path d="M3 6l5 5 5-5" stroke={D.textMuted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ height: 6, borderRadius: 999, overflow: 'hidden', background: 'rgba(0,77,64,0.08)', border: '1px solid rgba(0,77,64,0.08)' }}>
            <div style={{ width: `${main.percent}%`, height: '100%', borderRadius: 999, background: scoreFill(main), transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
          </div>
        </div>
        <div style={{
          overflow: 'hidden',
          maxHeight: scoreSubOffen ? '500px' : '0px',
          transition: 'max-height 0.35s cubic-bezier(0.22,1,0.36,1)',
        }}>
          <div style={{ paddingTop: compact ? 8 : 10 }}>
            {renderScoreBars(subs, compact)}
          </div>
        </div>
      </div>
    );
  };

  /* ── Screen 2: Stellenanzeige Eingabe ─────────── */
  if (ansicht === 'eingabe') {
    const profilLabel = (() => {
      const p = profile.find(p => p.dateiname === profilName);
      return p ? (p.vorname && p.nachname ? `${p.vorname} ${p.nachname}` : p.name) : 'Profil auswählen';
    })();

    return (
      <div style={{ width: '100%', height: '100dvh', background: D.bgGrad, display: 'flex', flexDirection: 'column', fontFamily: "'Geist', -apple-system, sans-serif", WebkitFontSmoothing: 'antialiased', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', paddingTop: 'calc(env(safe-area-inset-top,0px) + 14px)', borderBottom: `1px solid ${D.border}`, flexShrink: 0, background: D.bgGrad }}>
          <button onClick={onZurueck} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: D.textMuted, fontSize: 14, fontFamily: 'inherit', padding: 0, WebkitTapHighlightColor: 'transparent' }}>
            <IconChevronLeft />
            <span>Bewerbungen</span>
          </button>
          <div style={{ fontFamily: 'var(--cv-font-display)', fontSize: 20, fontWeight: 400, color: 'var(--cv-text)', letterSpacing: -0.3 }}>Stelle <em style={{ fontStyle: 'italic', color: 'var(--cv-amber)' }}>bearbeiten</em></div>
          <div style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--cv-teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--cv-amber)', flexShrink: 0 }}>{initialen}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px 16px', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 100px)', scrollbarWidth: 'none' }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontFamily: D.mono, letterSpacing: '0.14em', textTransform: 'uppercase', color: D.textDim, marginBottom: 8 }}>Profil</div>
            <div style={{ position: 'relative', background: D.card, border: `1px solid ${D.border}`, borderRadius: 14, boxShadow: '0 1px 4px rgba(28,24,18,0.05)' }}>
              <select value={profilName} onChange={e => setProfilName(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 2 }}>
                {profile.length === 0 && <option value="">Kein Profil vorhanden</option>}
                {profile.map(p => {
                  const n = p.vorname && p.nachname ? `${p.vorname} ${p.nachname}` : (p.name || p.dateiname);
                  return <option key={p.dateiname} value={p.dateiname}>{n}</option>;
                })}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
                <span style={{ fontSize: 15, color: D.text, fontWeight: 500 }}>{profilLabel}</span>
                <IconChevronDown />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontFamily: D.mono, letterSpacing: '0.14em', textTransform: 'uppercase', color: D.textDim }}>Stellenanzeige</div>
              <div style={{ display: 'flex', background: 'rgba(28,24,18,0.06)', borderRadius: 20, padding: 2, gap: 2 }}>
                {['text', 'url'].map(m => (
                  <button key={m} onClick={() => setModus(m)} style={{ padding: '5px 16px', borderRadius: 18, border: 'none', background: modus === m ? 'var(--cv-amber)' : 'transparent', color: modus === m ? 'var(--cv-teal)' : 'var(--cv-text-muted)', fontFamily: 'inherit', fontSize: 12, fontWeight: modus === m ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s', boxShadow: modus === m ? 'var(--cv-shadow-amber)' : 'none', WebkitTapHighlightColor: 'transparent' }}>
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {modus === 'text' ? (
              <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Stellenanzeige hier einfügen…" rows={8} style={{ width: '100%', padding: '14px 16px', boxSizing: 'border-box', border: `1.5px solid ${text ? 'var(--cv-border-warm)' : 'var(--cv-border)'}`, borderRadius: 14, background: 'var(--cv-bg-elevated)', fontFamily: 'inherit', fontSize: 14, color: 'var(--cv-text)', resize: 'none', outline: 'none', lineHeight: 1.55, transition: 'border-color 0.15s' }} />
            ) : (
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" style={{ width: '100%', padding: '14px 16px', boxSizing: 'border-box', border: `1.5px solid ${url ? 'var(--cv-border-warm)' : 'var(--cv-border)'}`, borderRadius: 14, background: 'var(--cv-bg-elevated)', fontFamily: 'inherit', fontSize: 14, color: 'var(--cv-text)', outline: 'none', transition: 'border-color 0.15s' }} />
            )}
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontFamily: D.mono, letterSpacing: '0.14em', textTransform: 'uppercase', color: D.textDim, marginBottom: 8 }}>Vorlagen</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SelectRow tag="CV" items={cvVorlagen} value={cvVorlage} onChange={setCvVorlage} fallback="CV Vorlage" />
              <SelectRow tag="AN" items={anVorlagen} value={anVorlage} onChange={setAnVorlage} fallback="Anschreiben Vorlage" />
            </div>
          </div>
        </div>
        <div style={{ flexShrink: 0, padding: '12px 16px', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 12px)', background: D.bgGrad, borderTop: `1px solid ${D.border}` }}>
          <button onClick={onAnalyseStarten} disabled={!kannStarten} style={{ width: '100%', padding: '16px', borderRadius: 'var(--cv-radius-pill)', border: 'none', background: kannStarten ? 'var(--cv-amber)' : 'rgba(0,0,0,0.06)', color: kannStarten ? 'var(--cv-teal)' : 'var(--cv-text-dim)', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: kannStarten ? 'pointer' : 'default', letterSpacing: -0.2, transition: 'all 0.18s var(--cv-ease)', WebkitTapHighlightColor: 'transparent', boxShadow: kannStarten ? 'var(--cv-shadow-amber)' : 'none' }}>
            Analyse starten
          </button>
        </div>
      </div>
    );
  }

  /* ── Screen 3: FitAnalyse (Stelle-Detail design) ── */
  if (ansicht === 'analyse') {
    const istLaufend = status === 'analyse';
    const istGeneriert = status === 'generiert';
    const istWarten = status === 'warten';
    const istFehler = status === 'fehler';

    const parsed = parseAnalyseText(analyse);
    const realScore = parsed.score;
    const verdict = buildFitVerdict(analyseStruktur || stelle?.analyse_struktur, realScore);
    const scoreRows = verdict.detailRows;
    const mainScore = verdict.overall?.percent ?? realScore;
    const scoreLabel = verdict.label || fitScoreLabel(analyseStruktur || stelle?.analyse_struktur);
    const displayScore = istLaufend ? Math.round(simProgress) : (mainScore ?? 94);
    const scoreColor = displayScore >= 80 ? '#059669' : displayScore >= 60 ? 'var(--cv-amber)' : '#DC2626';
    const scoreBg = displayScore >= 80
      ? 'linear-gradient(90deg, #4ADE80, #059669)'
      : displayScore >= 60
      ? 'linear-gradient(90deg, #FFBC00, #E6A800)'
      : 'linear-gradient(90deg, #F87171, #DC2626)';

    // Schritte aus echtem Backend-Index ableiten (6 Phasen bei Analyse, 4 bei Generierung)
    const realIdx = schrittIndex ?? 0;
    const realTotal = schrittTotal ?? 6;
    const pct = realTotal > 0 ? (realIdx / realTotal) * 100 : simProgress;
    const step1Done = !istLaufend || pct > 30;
    const step2Done = !istLaufend || pct > 60;
    const step3Done = !istLaufend && !istWarten;

    const company = stelle?.empfaenger_unternehmen || '';
    const stelleName = stelle?.name || 'Neue Stelle';
    const tags = [stelle?.url ? 'Link' : stelle?.text ? 'Text' : null].filter(Boolean);

    return (
      <div style={{ width: '100%', height: '100dvh', background: D.bgGrad, display: 'flex', flexDirection: 'column', fontFamily: "'Geist', -apple-system, sans-serif", WebkitFontSmoothing: 'antialiased', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 24px)' }}>
          <div style={{ padding: '16px 18px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0, background: 'var(--cv-teal)', color: 'var(--cv-amber)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>
              {stelleName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: D.text, letterSpacing: -0.4, lineHeight: 1.2, marginBottom: 3 }}>{stelleName}</div>
              {company && <div style={{ fontSize: 13, color: D.textMuted }}>{company}</div>}
              {tags.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {tags.map(t => (
                    <div key={t} style={{ padding: '3px 9px', borderRadius: 999, background: D.border, fontSize: 11, color: D.textMuted }}>{t}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ margin: '0 14px 10px', position: 'relative', background: D.card, border: (istLaufend || istGeneriert) ? '1.5px solid transparent' : `1px solid ${D.border}`, borderRadius: 18, boxShadow: (istLaufend || istGeneriert) ? '0 4px 24px rgba(255,188,0,0.1), 0 1px 6px rgba(28,24,18,0.07)' : '0 1px 4px rgba(28,24,18,0.06)', overflow: 'hidden' }}>
            {(istLaufend || istGeneriert) && (
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 1 }} fill="none">
                <rect rx="17" pathLength="1300" fill="none" stroke="#FFBC00" strokeWidth="12" strokeDasharray="100 1200" strokeLinecap="round" style={{ x: '6px', y: '6px', width: 'calc(100% - 12px)', height: 'calc(100% - 12px)', animation: 'msBorderSpin 6.5s linear infinite' }} />
              </svg>
            )}
            <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${D.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: 999, flexShrink: 0,
                  background: istLaufend ? D.amber : istFehler ? '#E85A4B' : scoreColor,
                  animation: istLaufend ? 'msDetailPulse 1.2s ease-in-out infinite' : 'none',
                  boxShadow: istLaufend ? '0 0 0 3px rgba(255,188,0,0.2)' : 'none',
                }} />
                <div style={{ fontFamily: D.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: D.text }}>
                  {istFehler ? 'Fehler aufgetreten'
                    : istLaufend ? 'Fit-Analyse läuft…'
                    : istGeneriert ? 'Wird generiert…'
                    : `Status: ${scoreLabel || 'Fit'} ${scoreRows[0]?.display || `${Math.round(displayScore / 10)}/10`} — Analyse abgeschlossen`}
                </div>
              </div>
              {!(istLaufend || scoreRows.length === 0) && (
                <div style={{ marginTop: 10 }}>
                  {/* Klartext-Urteil */}
                  {scoreLabel && (
                    <div style={{
                      fontFamily: 'var(--cv-font-display, Georgia, serif)',
                      fontSize: 16,
                      fontWeight: 400,
                      color: D.text,
                      letterSpacing: -0.2,
                      lineHeight: 1.25,
                      marginBottom: 10,
                    }}>
                      {scoreLabel}
                    </div>
                  )}

                  {/* Gesamt-Balken */}
                  {verdict.overall && renderScoreBars([verdict.overall])}

                  {/* Dimensions-Chips */}
                  {verdict.dimensions.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      {verdict.dimensions.map(dim => (
                        <div
                          key={dim.key}
                          title={dim.info}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '5px 10px 5px 7px', borderRadius: 9999,
                            background: 'linear-gradient(135deg, rgba(255,252,246,0.96), rgba(245,242,233,0.72))',
                            border: `1px solid ${D.border}`,
                            boxShadow: '0 2px 8px rgba(42,34,20,0.06), inset 0 1px 0 rgba(255,255,255,0.72)',
                          }}
                        >
                          <span style={{ width: 7, height: 7, borderRadius: 999, background: dim.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: D.text }}>
                            {dim.label}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: dim.color }}>
                            {dim.levelLabel}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Details (Zahlen) */}
                  {scoreRows.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        onClick={() => setScoreSubOffen(o => !o)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontFamily: 'inherit', fontSize: 10, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: D.textMuted, WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 16 16" fill="none"
                          style={{ transition: 'transform 0.22s ease', transform: scoreSubOffen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                          <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Details (Zahlen)
                      </button>
                      <div style={{
                        overflow: 'hidden',
                        maxHeight: scoreSubOffen ? '500px' : '0px',
                        transition: 'max-height 0.32s cubic-bezier(0.22,1,0.36,1)',
                      }}>
                        <div style={{ paddingTop: 10 }}>
                          {renderScoreBars(scoreRows)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {(istLaufend || istGeneriert) ? (
              <div style={{ padding: '14px 16px 16px' }}>
                {[
                  { label: schritt || 'Job Analyst', done: step1Done },
                  { label: 'Erfahrungen werden verglichen', done: step2Done },
                  { label: 'Skills werden abgeglichen', done: step3Done },
                ].map(({ label, done }, i) => {
                  const activeIdx = [step1Done, step2Done, step3Done].filter(Boolean).length;
                  const isActive = !done && i === activeIdx;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 2 ? 11 : 0, opacity: done ? 1 : isActive ? 1 : 0.3, transition: 'opacity 0.5s' }}>
                      {done ? (
                        <div style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0, background: D.amber, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5.5l2 2 4-4" stroke="var(--cv-teal)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      ) : (
                        <div style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0, border: `1.5px solid ${isActive ? 'var(--cv-amber)' : D.border}`, background: isActive ? 'rgba(255,188,0,0.07)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isActive && <div style={{ width: 6, height: 6, borderRadius: 999, background: D.amber, animation: 'msDetailPulse 1.2s ease-in-out infinite' }} />}
                        </div>
                      )}
                      <span style={{ fontSize: 13, fontWeight: done ? 600 : 500, color: done ? D.text : D.textMuted, letterSpacing: -0.1 }}>{label}</span>
                    </div>
                  );
                })}

                {/* Rotierende Tipp-Karte */}
                <div style={{ marginTop: 16, padding: '10px 13px', borderRadius: 12, background: 'rgba(0,77,64,0.06)', border: '1px solid rgba(0,77,64,0.1)' }}>
                  <div style={{ fontFamily: D.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cv-teal)', marginBottom: 5, opacity: 0.65 }}>
                    Tipp
                  </div>
                  <div key={tipIndex} style={{ fontSize: 12.5, color: D.textMuted, lineHeight: 1.5, animation: 'msAnalyseBlend 0.6s ease' }}>
                    {ANALYSE_TIPPS[tipIndex]}
                  </div>
                </div>
              </div>
            ) : (istWarten || istGeneriert) && analyse ? (
              parsed.staerken.length > 0 || parsed.optimierung.length > 0 || parsed.keywords.length > 0 || parsed.empfehlung ? (
                <>
                {parsed.keywords.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 14px 0' }}>
                    {parsed.keywords.slice(0, 8).map((keyword, i) => (
                      <span key={`${keyword}-${i}`} style={{ padding: '4px 8px', borderRadius: 999, background: 'var(--cv-amber-soft)', border: '1px solid var(--cv-border-warm)', color: 'var(--cv-teal)', fontSize: 10.5, fontWeight: 700 }}>
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
                {parsed.empfehlung && (
                  <div style={{ margin: parsed.keywords.length > 0 ? '10px 14px 0' : '12px 14px 0', padding: '10px 12px', borderRadius: 13, background: 'var(--cv-teal-soft)', border: '1px solid var(--cv-border-teal)' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: D.text, marginBottom: 5 }}>Empfehlung</div>
                    <div style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.5 }}>{parsed.empfehlung}</div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ padding: '14px 14px 16px', borderRight: `1px solid ${D.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 10, letterSpacing: -0.1 }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="#059669" strokeWidth="1.4"/><path d="M4 7l2 2 4-4" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Stärken-Profil
                    </div>
                    {(parsed.staerken.length > 0 ? parsed.staerken : ['Qualifikationen geprüft']).slice(0, 5).map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 7 }}>
                        <div style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, marginTop: 4, background: '#059669' }} />
                        <div style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.45 }}>{s}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '14px 14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: D.text, marginBottom: 10, letterSpacing: -0.1 }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="var(--cv-amber)" strokeWidth="1.4"/><path d="M7 4v3.5M7 9.5v.5" stroke="var(--cv-amber)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      Optimierung
                    </div>
                    {(parsed.optimierung.length > 0 ? parsed.optimierung : ['Profil weiter ausbauen']).slice(0, 4).map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 7 }}>
                        <div style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, marginTop: 4, background: 'var(--cv-amber)' }} />
                        <div style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.45 }}>{s}</div>
                      </div>
                    ))}
                  </div>
                </div>
                </>
              ) : (
                <div style={{ padding: '14px 16px' }}>
                  <pre style={{ fontFamily: D.mono, fontSize: 11, color: D.textMuted, whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0 }}>{analyse}</pre>
                </div>
              )
            ) : istFehler ? (
              <div style={{ padding: '14px 16px', fontSize: 13, color: '#E85A4B' }}>{fehler || 'Ein unbekannter Fehler ist aufgetreten.'}</div>
            ) : null}
          </div>
          <div style={{ margin: '4px 14px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {istWarten && (
              <button onClick={onWeiterGenerieren} style={{ width: '100%', padding: '14px 18px', borderRadius: 'var(--cv-radius-pill)', border: 'none', background: 'var(--cv-amber)', color: 'var(--cv-teal)', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, letterSpacing: -0.2, boxShadow: 'var(--cv-shadow-amber)' }}>
                Bewerbung generieren
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="var(--cv-teal)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            )}
            {(istWarten || istFehler) && (
              <>
                {istFehler && retryMoeglich && (
                  <button onClick={onRetryGenerieren} style={{ width: '100%', padding: '14px 18px', borderRadius: 'var(--cv-radius-pill)', border: '1.5px solid var(--cv-teal)', background: 'var(--cv-teal-soft)', color: 'var(--cv-teal)', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, letterSpacing: -0.2, marginBottom: 8 }}>
                    Erneut versuchen
                  </button>
                )}
              <button onClick={onAbbrechen} style={{ width: '100%', padding: '13px 18px', borderRadius: 16, border: `1px solid ${D.border}`, background: 'transparent', color: D.textMuted, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer', textAlign: 'center' }}>
                Abbrechen
              </button>
              </>
            )}
          </div>
          {istGeneriert && (
            <div style={{ padding: '10px 14px 28px' }}>
              {/* Tab switcher */}
              <div style={{ display: 'flex', background: 'rgba(0,0,0,0.05)', borderRadius: 999, padding: 3, marginBottom: 14 }}>
                {[{ label: 'Lebenslauf', idx: 0 }, { label: 'Anschreiben', idx: 1 }].map(({ label, idx }) => (
                  <button key={idx} onClick={() => setDokSlide(idx)} style={{
                    flex: 1, padding: '7px 10px', borderRadius: 999, border: 'none',
                    background: dokSlide === idx ? '#FFBC00' : 'transparent',
                    color: dokSlide === idx ? '#004D40' : 'rgba(28,24,18,0.45)',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: dokSlide === idx ? 700 : 500,
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    WebkitTapHighlightColor: 'transparent', letterSpacing: -0.1,
                  }}>{label}</button>
                ))}
              </div>
              {dokSlide === 0 ? (
                /* ── CV Skeleton ─────────────────────────────── */
                <div style={{
                  background: 'linear-gradient(160deg,#FFFDF8 0%,#FAFAF3 100%)',
                  borderRadius: 18, overflow: 'hidden', position: 'relative',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.10),0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(255,188,0,0.22)',
                }}>
                  <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
                    background: 'linear-gradient(100deg,transparent 0%,rgba(255,188,0,0.11) 45%,transparent 68%)',
                    animation: 'msSkelShimmer 2.3s ease-in-out infinite' }} />
                  {/* Profile header */}
                  <div style={{ padding: '20px 18px 16px', display: 'flex', gap: 14, borderBottom: '1.5px solid rgba(255,188,0,0.18)' }}>
                    <div style={{ width: 58, height: 68, borderRadius: 12, flexShrink: 0, background: 'rgba(0,77,64,0.1)', animation: 'msSkelPulse 1.65s ease-in-out infinite' }} />
                    <div style={{ flex: 1, paddingTop: 4 }}>
                      <div style={{ height: 15, borderRadius: 999, background: 'rgba(0,77,64,0.14)', width: '72%', marginBottom: 9, animation: 'msSkelWave 1.7s ease-in-out infinite' }} />
                      <div style={{ height: 9, borderRadius: 999, background: 'rgba(0,77,64,0.09)', width: '50%', marginBottom: 11 }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[46, 62, 44].map((w, i) => <div key={i} style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.07)', width: w }} />)}
                      </div>
                    </div>
                  </div>
                  {/* Sections */}
                  {[
                    { t: 37, lines: [91, 73, 95, 67] },
                    { t: 27, lines: [79, 93, 61] },
                    { t: 43, lines: [86, 72, 88] },
                    { t: 33, lines: [94, 67] },
                  ].map((sec, si) => (
                    <div key={si} style={{ padding: `${si === 0 ? 14 : 10}px 18px ${si === 3 ? 22 : 5}px` }}>
                      <div style={{ height: 7, borderRadius: 4, width: `${sec.t}%`, marginBottom: 10, background: 'rgba(255,188,0,0.4)', animation: `msSkelWave 1.7s ease-in-out ${si * 0.2}s infinite` }} />
                      {sec.lines.map((w, li) => (
                        <div key={li} style={{ height: 5, borderRadius: 999, marginBottom: 6, width: `${w}%`, background: 'rgba(0,77,64,0.08)', animation: `msSkelWave 1.7s ease-in-out ${si * 0.2 + li * 0.09}s infinite` }} />
                      ))}
                    </div>
                  ))}
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72, background: 'linear-gradient(to bottom,transparent,rgba(250,249,243,0.97))', pointerEvents: 'none' }} />
                </div>
              ) : (
                /* ── Anschreiben Skeleton ────────────────────── */
                <div style={{
                  background: 'linear-gradient(160deg,#FFFDF8 0%,#FAFAF3 100%)',
                  borderRadius: 18, overflow: 'hidden', position: 'relative',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.10),0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(255,188,0,0.22)',
                }}>
                  <div style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
                    background: 'linear-gradient(100deg,transparent 0%,rgba(255,188,0,0.11) 45%,transparent 68%)',
                    animation: 'msSkelShimmer 2.3s ease-in-out 0.45s infinite' }} />
                  <div style={{ padding: '20px 18px 22px' }}>
                    {/* Sender */}
                    <div style={{ marginBottom: 16 }}>
                      {[65, 48, 56].map((w, i) => <div key={i} style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.09)', width: `${w}%`, marginBottom: 5.5, animation: `msSkelWave 1.7s ease-in-out ${i * 0.1}s infinite` }} />)}
                    </div>
                    {/* Recipient */}
                    <div style={{ marginBottom: 16 }}>
                      {[49, 55, 41].map((w, i) => <div key={i} style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.09)', width: `${w}%`, marginBottom: 5.5 }} />)}
                    </div>
                    {/* Date */}
                    <div style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.07)', width: '28%', marginBottom: 16 }} />
                    {/* Subject */}
                    <div style={{ height: 11, borderRadius: 6, background: 'rgba(0,77,64,0.14)', width: '79%', marginBottom: 16, animation: 'msSkelWave 1.7s ease-in-out infinite' }} />
                    {/* Greeting */}
                    <div style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.08)', width: '52%', marginBottom: 14 }} />
                    {/* Paragraphs */}
                    {[[95, 88, 92, 77], [90, 85, 94, 61], [87, 93, 73]].map((lines, pi) => (
                      <div key={pi} style={{ marginBottom: 13 }}>
                        {lines.map((w, li) => <div key={li} style={{ height: 5.5, borderRadius: 999, marginBottom: 5.5, background: 'rgba(0,77,64,0.07)', width: `${w}%`, animation: `msSkelWave 1.7s ease-in-out ${pi * 0.22 + li * 0.09}s infinite` }} />)}
                      </div>
                    ))}
                    {/* Closing */}
                    <div style={{ height: 7, borderRadius: 999, background: 'rgba(0,77,64,0.08)', width: '34%', marginBottom: 10 }} />
                    <div style={{ height: 9, borderRadius: 999, background: 'rgba(0,77,64,0.13)', width: '46%' }} />
                  </div>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72, background: 'linear-gradient(to bottom,transparent,rgba(250,249,243,0.97))', pointerEvents: 'none' }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Screen 4/5: Ergebnis ── */
  {
    const ergebnisParsed = parseAnalyseText(analyse || stelle?.analyse);
    const ergebnisScore = ergebnisParsed.score;
    const ergebnisVerdict = buildFitVerdict(analyseStruktur || stelle?.analyse_struktur, ergebnisScore);
    const ergebnisScoreRows = ergebnisVerdict.detailRows;
    const ergebnisMainScore = ergebnisVerdict.overall?.percent ?? ergebnisScore;
    const ergebnisScoreLabel = ergebnisVerdict.label || fitScoreLabel(analyseStruktur || stelle?.analyse_struktur);
    const lightScoreColor = ergebnisMainScore != null ? (ergebnisMainScore >= 80 ? '#059669' : ergebnisMainScore >= 60 ? '#D97706' : '#DC2626') : '#059669';
    const lightScoreBg = ergebnisMainScore != null ? (ergebnisMainScore >= 80 ? 'linear-gradient(90deg,#4ADE80,#059669)' : ergebnisMainScore >= 60 ? 'linear-gradient(90deg,#F59E0B,#D97706)' : 'linear-gradient(90deg,#F87171,#DC2626)') : 'linear-gradient(90deg,#4ADE80,#059669)';
    const hatAnalyse = ergebnisParsed.staerken.length > 0 || ergebnisParsed.optimierung.length > 0;
    const isCV = dokSlide === 0;
    const exportLaedt = isCV ? pdfLaedt.cv : pdfLaedt.an;
    const exportDisabled = exportLaedt || (isCV ? !(stelle?.cv_data || stelle?.cv_rohtext) : !stelle?.an_rohtext);
    const onExport = isCV ? druckCV : druckAN;
    const bewerbungsStatus = stelle?.bewerbungs_status || '';
    const aktiverStatusOpt = STATUS_OPTIONEN.find(o => o.key === bewerbungsStatus) ?? null;

    const onBewStatusAendern = (neuerStatus) => {
      onGeaendert({ ...stelle, bewerbungs_status: neuerStatus }, 'update');
      updateStelle(stelle.id, { bewerbungs_status: neuerStatus }).catch(() => {});
      setStatusDropdownOffen(false);
    };

    return (
      <div style={{ position: 'relative', width: '100%', height: '100dvh', background: D.bgGrad, fontFamily: "'Geist', -apple-system, sans-serif", WebkitFontSmoothing: 'antialiased', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {aktiverStatusOpt && (
          <div style={{ position: 'absolute', top: -20, right: -20, opacity: 0.04, pointerEvents: 'none', zIndex: 0, transform: 'rotate(-15deg)', filter: 'blur(2px)' }}>
            <aktiverStatusOpt.Icon size={260} color={aktiverStatusOpt.color} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', paddingTop: 'calc(env(safe-area-inset-top,0px) + 12px)', borderBottom: `1px solid ${D.border}`, flexShrink: 0, position: 'relative', zIndex: 10, background: 'transparent' }}>

          <div style={{ flex: 1, textAlign: 'center', overflow: 'hidden', padding: '0 8px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: D.text, letterSpacing: -0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stelle?.name || stelle?.empfaenger_unternehmen || 'Stelle'}
            </div>
          </div>
          <div ref={statusBtnRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setStatusDropdownOffen(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px 7px 10px', borderRadius: 30, border: `1.5px solid ${aktiverStatusOpt ? `${aktiverStatusOpt.color}45` : 'rgba(28,24,18,0.15)'}`, background: aktiverStatusOpt ? `${aktiverStatusOpt.color}0D` : 'rgba(28,24,18,0.04)', color: aktiverStatusOpt ? aktiverStatusOpt.color : 'rgba(28,24,18,0.4)', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', WebkitTapHighlightColor: 'transparent', transition: 'all 0.2s', boxShadow: aktiverStatusOpt ? `0 2px 8px ${aktiverStatusOpt.color}20` : 'none' }}>
              {aktiverStatusOpt ? (
                <>
                  <aktiverStatusOpt.Icon size={13} color={aktiverStatusOpt.color} />
                  <span style={{ maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{aktiverStatusOpt.label}</span>
                </>
              ) : (
                <>
                  <StatusSendeIcon size={13} color="rgba(28,24,18,0.3)" />
                  <span>Abgeschickt</span>
                </>
              )}
            </button>
            {statusDropdownOffen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 192, background: D.card, border: `1.5px solid ${D.border}`, borderRadius: 18, padding: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)', zIndex: 100 }}>
                {STATUS_OPTIONEN.map(opt => (
                  <button key={opt.key} onClick={() => onBewStatusAendern(opt.key)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: 'none', width: '100%', textAlign: 'left', background: bewerbungsStatus === opt.key ? `${opt.color}12` : 'transparent', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: bewerbungsStatus === opt.key ? 700 : 500, color: bewerbungsStatus === opt.key ? opt.color : D.textMuted, WebkitTapHighlightColor: 'transparent', transition: 'background 0.15s' }}>
                    <opt.Icon size={16} color={opt.color} />
                    <span style={{ flex: 1 }}>{opt.label}</span>
                    {bewerbungsStatus === opt.key && <svg width="12" height="9" viewBox="0 0 13 10" fill="none"><path d="M1.5 5l3.5 3.5 6.5-7" stroke={opt.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', position: 'relative', zIndex: 1 }}>
          <div style={{ margin: '12px 14px 8px', background: D.card, borderRadius: 16, boxShadow: '0 1px 10px rgba(0,0,0,0.06)', border: `1px solid ${D.border}`, overflow: 'hidden' }}>
            {/* ── Score header row ── */}
            <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ width: 9, height: 9, borderRadius: 999, flexShrink: 0, marginTop: 3, background: lightScoreColor }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: D.text }}>
                  {ergebnisMainScore != null ? `${ergebnisScoreLabel || 'FIT'} ${ergebnisScoreRows[0]?.display || `${Math.round(ergebnisMainScore / 10)}/10`} — Analyse abgeschlossen` : 'Analyse abgeschlossen'}
                </div>
                {ergebnisScoreRows.length > 0 ? (
                  <div style={{ marginTop: 9 }}>
                    {renderCollapsibleScoreBars(ergebnisScoreRows, true)}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
                    <div style={{ flex: 1, height: 5, background: 'rgba(0,0,0,0.07)', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 999, background: lightScoreBg, width: ergebnisMainScore != null ? `${ergebnisMainScore}%` : '70%', transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
                    </div>
                    <div style={{ fontFamily: D.mono, fontSize: 13, fontWeight: 700, color: lightScoreColor, flexShrink: 0, letterSpacing: -0.3 }}>
                      {ergebnisMainScore != null ? `${Math.round(ergebnisMainScore / 10)}/10` : '—'}
                    </div>
                  </div>
                )}
              </div>
              {hatAnalyse && (
                <button
                  onClick={() => { setFitOffen(o => { if (!o) setActiveSlide(0); return !o; }); }}
                  style={{ background: 'none', border: 'none', padding: '2px 0 2px 6px', cursor: 'pointer', flexShrink: 0, color: D.textMuted, WebkitTapHighlightColor: 'transparent' }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block', transition: 'transform 0.2s', transform: fitOffen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>

            {ergebnisParsed.keywords.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 12px' }}>
                {ergebnisParsed.keywords.slice(0, 8).map((keyword, i) => (
                  <span key={`${keyword}-${i}`} style={{ padding: '4px 8px', borderRadius: 999, background: 'rgba(201,136,42,0.12)', border: `1px solid rgba(201,136,42,0.22)`, color: D.text, fontSize: 10.5, fontWeight: 700 }}>
                    {keyword}
                  </span>
                ))}
              </div>
            )}

            {(() => {
              const raw = (analyseStruktur || stelle?.analyse_struktur)?.empfehlung ?? ergebnisParsed.empfehlung;
              const items = Array.isArray(raw) ? raw.filter(Boolean) : raw ? [String(raw).trim()] : [];
              return items.length > 0 ? (
                <div style={{ margin: '0 14px 12px', padding: '10px 12px', borderRadius: 13, background: 'rgba(0,77,77,0.06)', border: `1px solid rgba(0,77,77,0.12)` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: D.text, marginBottom: 5 }}>Empfehlung</div>
                  <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {items.map((item, i) => (
                      <li key={i} style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.5 }}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null;
            })()}

            {/* ── Collapsible: dots + swipe slides ── */}
            {fitOffen && hatAnalyse && (
              <>
                <div style={{ height: 1, background: D.border, margin: '0 14px' }} />
                {/* Dots indicator */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, padding: '10px 0 8px' }}>
                  {[0, 1].map(i => (
                    <div
                      key={i}
                      onClick={() => { const el = swipeRef.current; if (el) { el.style.transition = 'transform 0.38s cubic-bezier(0.22,1,0.36,1)'; el.style.transform = `translateX(${-i * 100}%)`; } setActiveSlide(i); }}
                      style={{ width: activeSlide === i ? 16 : 6, height: 6, borderRadius: 999, background: activeSlide === i ? (i === 0 ? D.green : D.amber) : D.border, transition: 'all 0.2s ease', cursor: 'pointer' }}
                    />
                  ))}
                </div>
                {/* Swipe track — transform-based, no scroll snap */}
                <div data-swipe-internal style={{ overflow: 'hidden', touchAction: 'pan-y' }}>
                  <div
                    ref={el => { swipeRef.current = el; }}
                    onPointerDown={(e) => {
                      const el = swipeRef.current;
                      if (!el) return;
                      dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, dx: 0, dirLock: null, pid: e.pointerId };
                      el.setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                      if (!dragRef.current.active) return;
                      const el = swipeRef.current;
                      if (!el) return;
                      const dx = e.clientX - dragRef.current.startX;
                      const dy = e.clientY - dragRef.current.startY;
                      if (!dragRef.current.dirLock) {
                        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                        dragRef.current.dirLock = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
                        if (dragRef.current.dirLock === 'h') {
                          el.style.transition = 'none';
                        } else {
                          // Vertical — release capture so browser handles native scroll
                          dragRef.current.active = false;
                          try { el.releasePointerCapture(dragRef.current.pid); } catch (_) {}
                          el.style.transition = 'transform 0.38s cubic-bezier(0.22,1,0.36,1)';
                          el.style.transform = `translateX(${-activeSlide * 100}%)`;
                          return;
                        }
                      }
                      if (dragRef.current.dirLock !== 'h') return;
                      dragRef.current.dx = dx;
                      dragRef.current.velX = e.clientX - (dragRef.current.lastX ?? e.clientX);
                      dragRef.current.lastX = e.clientX;
                      // rubber-band when dragging past the first/last slide
                      const overEdge = (dx > 0 && activeSlide === 0) || (dx < 0 && activeSlide === 1);
                      const rubber = overEdge ? dx / 3.5 : dx;
                      el.style.transform = `translateX(calc(${-activeSlide * 100}% + ${rubber}px))`;
                    }}
                    onPointerUp={() => {
                      if (!dragRef.current.active) return;
                      const el = swipeRef.current;
                      if (!el) return;
                      dragRef.current.active = false;
                      const dir = dragRef.current.dirLock;
                      const dx = dragRef.current.dx;
                      const vel = dragRef.current.velX ?? 0;
                      el.style.transition = 'transform 0.38s cubic-bezier(0.22,1,0.36,1)';
                      if (dir !== 'h') {
                        el.style.transform = `translateX(${-activeSlide * 100}%)`;
                        return;
                      }
                      const threshold = el.offsetWidth * 0.18;
                      let next = activeSlide;
                      if ((dx < -threshold || vel < -5) && activeSlide === 0) next = 1;
                      if ((dx > threshold || vel > 5) && activeSlide === 1) next = 0;
                      el.style.transform = `translateX(${-next * 100}%)`;
                      setActiveSlide(next);
                    }}
                    onPointerCancel={() => {
                      const el = swipeRef.current;
                      if (!el) return;
                      dragRef.current.active = false;
                      el.style.transition = 'transform 0.38s cubic-bezier(0.22,1,0.36,1)';
                      el.style.transform = `translateX(${-activeSlide * 100}%)`;
                    }}
                    style={{ display: 'flex', transform: `translateX(${-activeSlide * 100}%)`, transition: 'transform 0.38s cubic-bezier(0.22,1,0.36,1)', userSelect: 'none', cursor: 'grab', willChange: 'transform' }}
                  >
                    {/* Slide 1: Stärken */}
                    <div style={{ flex: '0 0 100%', minWidth: 0, padding: '4px 14px 14px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: D.green, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Stärken</div>
                      {ergebnisParsed.staerken.slice(0, 5).map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                          <div style={{ width: 4, height: 4, borderRadius: 999, background: D.green, flexShrink: 0, marginTop: 5 }} />
                          <div style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.45 }}>{s}</div>
                        </div>
                      ))}
                    </div>
                    {/* Slide 2: Optimierung */}
                    <div style={{ flex: '0 0 100%', minWidth: 0, padding: '4px 14px 14px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: D.amber, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Optimierung</div>
                      {ergebnisParsed.optimierung.slice(0, 4).map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                          <div style={{ width: 4, height: 4, borderRadius: 999, background: D.amber, flexShrink: 0, marginTop: 5 }} />
                          <div style={{ fontSize: 11.5, color: D.textMuted, lineHeight: 1.45 }}>{s}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div style={{ margin: '4px 14px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Dokument Tab Indicator */}
            <div style={{ flex: 1, display: 'flex', background: 'rgba(0,0,0,0.05)', borderRadius: 999, padding: 3, maxWidth: 230 }}>
              {[{ label: 'Lebenslauf', idx: 0 }, { label: 'Anschreiben', idx: 1 }].map(({ label, idx }) => (
                <button
                  key={idx}
                  onClick={() => {
                    const el = dokSwipeRef.current;
                    if (el) {
                      el.style.transition = 'transform 0.28s ease';
                      el.style.transform = `translateX(${-idx * 100}%)`;
                      setTimeout(() => { if (el) el.style.transition = ''; }, 300);
                    }
                    setDokSlide(idx);
                  }}
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 999, border: 'none',
                    background: dokSlide === idx ? '#FFBC00' : 'transparent',
                    color: dokSlide === idx ? '#004D40' : 'rgba(28,24,18,0.45)',
                    fontFamily: 'inherit', fontSize: 13,
                    fontWeight: dokSlide === idx ? 700 : 500,
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: -0.1,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button onClick={onExport} disabled={exportDisabled} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 14px', borderRadius: 30, flexShrink: 0, border: `1.5px solid ${exportDisabled ? 'rgba(28,24,18,0.08)' : 'rgba(0,77,64,0.25)'}`, background: exportDisabled ? 'transparent' : 'rgba(0,77,64,0.05)', color: exportDisabled ? D.textDim : '#004D40', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: exportDisabled ? 'not-allowed' : 'pointer', WebkitTapHighlightColor: 'transparent' }}>
              {exportLaedt
                ? <span className="msf-layout-spinner" style={{ width: 12, height: 12, border: '1.5px solid rgba(0,77,64,0.2)', borderTopColor: '#004D40' }} aria-hidden="true" />
                : <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 1v8M4 6l3 3 3-3M1 10v1a2 2 0 002 2h8a2 2 0 002-2v-1" stroke={exportDisabled ? D.textDim : '#004D40'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              {exportLaedt ? 'Lädt…' : 'Export'}
            </button>
          </div>
          {/* Dokument Swipe Track */}
          <div data-swipe-internal style={{ overflow: 'hidden', touchAction: 'pan-y' }}>
            <div
              ref={el => { dokSwipeRef.current = el; }}
              onTouchStart={(e) => {
                dokTouchStartX.current = e.touches[0].clientX;
                dokTouchStartY.current = e.touches[0].clientY;
                dokTouchLastX.current = e.touches[0].clientX;
                dokDirLock.current = null;
              }}
              onTouchMove={(e) => {
                if (dokTouchStartX.current === null) return;
                const dx = e.touches[0].clientX - dokTouchStartX.current;
                const dy = e.touches[0].clientY - dokTouchStartY.current;
                dokTouchLastX.current = e.touches[0].clientX;
                // Lock direction after 10px of movement
                if (!dokDirLock.current) {
                  if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                  dokDirLock.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
                }
                if (dokDirLock.current !== 'h') return;
                const el = dokSwipeRef.current;
                if (el) el.style.transform = `translateX(calc(${-dokSlide * 100}% + ${dx}px))`;
              }}
              onTouchEnd={() => {
                if (dokTouchStartX.current === null) return;
                const dir = dokDirLock.current;
                const dx = (dokTouchLastX.current || 0) - dokTouchStartX.current;
                dokTouchStartX.current = null;
                dokDirLock.current = null;
                const el = dokSwipeRef.current;
                if (dir !== 'h') {
                  // Vertical gesture — snap back without slide change
                  if (el) {
                    el.style.transition = 'transform 0.2s ease';
                    el.style.transform = `translateX(${-dokSlide * 100}%)`;
                    setTimeout(() => { if (el) el.style.transition = ''; }, 220);
                  }
                  return;
                }
                const threshold = 50;
                let next = dokSlide;
                if (dx < -threshold && dokSlide === 0) next = 1;
                if (dx > threshold && dokSlide === 1) next = 0;
                if (el) {
                  el.style.transition = 'transform 0.28s ease';
                  el.style.transform = `translateX(${-next * 100}%)`;
                  setTimeout(() => { if (el) el.style.transition = ''; }, 300);
                }
                setDokSlide(next);
              }}
              style={{
                display: 'flex',
                transform: `translateX(${-dokSlide * 100}%)`,
                willChange: 'transform',
                userSelect: 'none',
              }}
            >
              {/* Slide 0: CV */}
              <div style={{ flex: '0 0 100%', minWidth: 0 }}>
                <MobileCVVorschau
                  stelle={stelle}
                  ref={cvRef}
                  onRebuildDone={(neuesDaten) => {
                    if (!neuesDaten) { onGeaendert({ ...stelle }, 'update'); return; }
                    if (typeof neuesDaten === 'object' && !Array.isArray(neuesDaten) && neuesDaten.sektionen) {
                      onGeaendert({ ...stelle, cv_data: neuesDaten }, 'update');
                    } else {
                      onGeaendert({ ...stelle, cv_rohtext: neuesDaten }, 'update');
                    }
                  }}
                  onFotoChange={(fotoUrl) => {
                    onGeaendert({ ...stelle, foto_url: fotoUrl }, 'update');
                  }}
                  onEditToggle={onSektionBearbeiten}
                />
              </div>
              {/* Slide 1: Anschreiben */}
              <div style={{ flex: '0 0 100%', minWidth: 0 }}>
                {/* Hinweis auf mehrere Seiten entfernt (2026-08-09) — hier genauso wie
                    im Editor. Mehrere Seiten sind kein Fehler mehr. */}
                <MobileAnschreibenVorschau
                  stelle={stelle}
                  ref={anRef}
                  onRebuildDone={(neuerRohtext) =>
                    onGeaendert(neuerRohtext ? { ...stelle, an_rohtext: neuerRohtext } : { ...stelle }, 'update')
                  }
                />
              </div>
            </div>
          </div>
          <div style={{ padding: '10px 18px 4px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: D.textDim, letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0 }}>Powered by Anthropic</span>
          </div>
          <div style={{ height: !sheetIsOpen && dokSlide === 0 && stelle?.cv_data?.sektionen?.length > 0 ? 'calc(env(safe-area-inset-bottom, 0px) + 72px)' : 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }} />
        </div>
        {/* Footer section quick-nav */}
        {!sheetIsOpen && dokSlide === 0 && stelle?.cv_data?.sektionen?.length > 0 && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
            paddingTop: 8,
            background: 'linear-gradient(to top, rgba(243,238,227,0.98) 60%, rgba(243,238,227,0) 100%)',
            zIndex: 10,
          }}>
            <div style={{
              display: 'flex', gap: 6,
              overflowX: 'auto', padding: '2px 14px',
              scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
            }}>
              {stelle.cv_data.sektionen.map((sek, i) => (
                <button
                  key={i}
                  onClick={() => onSektionBearbeiten(i, sek)}
                  style={{
                    flexShrink: 0,
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: `1.5px solid rgba(0,77,64,0.18)`,
                    background: 'rgba(255,255,255,0.82)',
                    color: '#004D40',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                    WebkitTapHighlightColor: 'transparent',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s, transform 0.1s',
                  }}
                >
                  {sek.name || sek.typ}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default MobileStelleFlow;
